/* ==========================================================================
   nl-marquee.js — one shared marquee behavior for every gn- section.
   Seamless horizontal auto-scroll driven by the Web Animations API (self-
   contained — no per-section @keyframes to coordinate). Clones the track
   content enough to fill 2× the viewport so the loop is gapless.

   Root: [data-nl-marquee]  value = scroll speed in px/sec (default 60)
   Track: [data-nl-marquee-track]  (holds the items once; JS duplicates)

   Pauses on hover/focus; [data-nl-marquee-touch-pause] on the root also pauses
   while a finger is on the row; [data-nl-marquee-below="1024"] runs it only
   below that width (static and uncloned at and above it). Respects prefers-reduced-motion (no animation — the
   row stays static and horizontally scrollable via the section's CSS).
   Editor lifecycle: guards double-init, re-inits on shopify:section:load,
   cancels the animation on shopify:section:unload.
   ========================================================================== */
(function () {
  'use strict';
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];

  function Marquee(root) {
    this.root = root;
    this.track = root.querySelector('[data-nl-marquee-track]');
    this.speed = parseFloat(root.getAttribute('data-nl-marquee')) || 60;
    this.anim = null;
    this._bound = [];
    if (!this.track || !this.track.children.length) return;
    // Optional: run only below a breakpoint (no-op without the attribute). At and
    // above it the row stays as authored — no clones, no animation — so the
    // section's own CSS can lay the same items out statically.
    var below = parseInt(root.getAttribute('data-nl-marquee-below'), 10);
    if (below > 0 && window.matchMedia) {
      var self = this;
      this.mq = window.matchMedia('(min-width: ' + below + 'px)');
      this._onMq = function () { if (self.mq.matches) self.stop(); else if (!self.running) self.start(); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', this._onMq); else this.mq.addListener(this._onMq);
      this._onMq();
    } else {
      this.start();
    }
  }

  Marquee.prototype.start = function () { this.running = true; this.init(); };

  // Undo init: cancel the animation, drop listeners and the cloned sets.
  Marquee.prototype.stop = function () {
    if (this.anim) { this.anim.cancel(); this.anim = null; }
    this._bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2]); });
    this._bound = [];
    if (this.originals) {
      var keep = this.originals;
      Array.prototype.slice.call(this.track.children).forEach(function (n) { if (keep.indexOf(n) < 0) n.remove(); });
    }
    this.running = false;
  };

  Marquee.prototype.on = function (el, ev, fn) {
    el.addEventListener(ev, fn); this._bound.push([el, ev, fn]);
  };

  Marquee.prototype.init = function () {
    var self = this;
    // Snapshot the original set, then clone whole sets until we cover 2× viewport.
    this.originals = Array.prototype.slice.call(this.track.children);
    var oneSetWidth = this.track.scrollWidth;
    if (oneSetWidth <= 0) return;
    var target = this.root.offsetWidth * 2 + oneSetWidth;
    var guard = 0;
    while (this.track.scrollWidth < target && guard < 20) {
      this.originals.forEach(function (node) {
        var c = node.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        self.track.appendChild(c);
      });
      guard++;
    }
    this._oneSetWidth = oneSetWidth;

    if (REDUCE) return; // static, scrollable — no animation

    var duration = (oneSetWidth / this.speed) * 1000;
    this.anim = this.track.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-' + oneSetWidth + 'px)' }],
      { duration: duration, iterations: Infinity, easing: 'linear' }
    );

    this.on(this.root, 'mouseenter', function () { if (self.anim) self.anim.pause(); });
    this.on(this.root, 'mouseleave', function () { if (self.anim) self.anim.play(); });
    this.on(this.root, 'focusin', function () { if (self.anim) self.anim.pause(); });
    this.on(this.root, 'focusout', function () { if (self.anim) self.anim.play(); });

    // Optional: pause while a finger rests on the row (no-op without the attribute).
    if (this.root.hasAttribute('data-nl-marquee-touch-pause')) {
      var tPause = function () { if (self.anim) self.anim.pause(); };
      var tPlay = function () { if (self.anim) self.anim.play(); };
      this.on(this.root, 'touchstart', tPause);
      this.on(this.root, 'touchend', tPlay);
      this.on(this.root, 'touchcancel', tPlay);
    }
  };

  Marquee.prototype.destroy = function () {
    if (this.anim) { this.anim.cancel(); this.anim = null; }
    this._bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2]); });
    this._bound = [];
    if (this.mq) {
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this._onMq); else this.mq.removeListener(this._onMq);
      this.mq = null;
    }
    this.root.__mtMarquee = null;
  };

  function initWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-nl-marquee]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__mtMarquee) return;
      root.__mtMarquee = new Marquee(root);
      instances.push(root.__mtMarquee);
    });
  }
  function destroyWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-nl-marquee]');
    Array.prototype.forEach.call(roots, function (root) { if (root.__mtMarquee) root.__mtMarquee.destroy(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initWithin(document); });
  else initWithin(document);
  document.addEventListener('shopify:section:load', function (e) { initWithin(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyWithin(e.target); });
})();
